/**
 * JS 구문 검사 — 결정 #30. Acorn 파서를 **lazy-load**해 실제 JavaScript 구문 오류를
 * 잡는다(별도 청크라 초기 번들 0 영향, 결정 #1). 균형 검사기(core analyzeBalance)는
 * 괄호·문자열만 보지만, 이쪽은 진짜 파싱이라 ASI로 못 살리는 세미콜론 오류, 잘못된
 * 토큰, 예약어 오용 등을 잡는다.
 *
 * core가 아니라 web에 있는 이유(결정 #10): Acorn은 npm 의존성이라 플랫폼 중립 core에
 * 넣을 수 없다. core 수리 루프의 validate는 비동기를 받으므로 이 함수를 그대로 주입한다.
 *
 * 로드 실패(저사양·네트워크) 시 빈 배열을 반환해 "검사 생략"으로 graceful 폴백한다.
 */

type AcornParse = (src: string, opts: Record<string, unknown>) => unknown;

let acornPromise: Promise<AcornParse | null> | null = null;

/** Acorn parse를 한 번만 lazy-load(여러 호출이 공유). 실패 시 null. */
function loadAcorn(): Promise<AcornParse | null> {
  if (!acornPromise) {
    acornPromise = import("acorn")
      .then((m) => m.parse as unknown as AcornParse)
      .catch(() => null);
  }
  return acornPromise;
}

/** Acorn SyntaxError는 loc(줄/열)와 message를 가진다(런타임 형태). */
interface AcornSyntaxError {
  message: string;
  loc?: { line: number; column: number };
}

function isSyntaxError(e: unknown): e is AcornSyntaxError {
  return !!e && typeof e === "object" && "message" in e;
}

/**
 * JS 소스의 구문 오류 메시지 배열을 반환한다(빈 배열 = 유효 또는 검사 생략).
 * 최신 문법을 관대하게 받도록 ecmaVersion=latest, module 소스 타입으로 파싱한다.
 */
export async function checkJsSyntax(source: string): Promise<string[]> {
  if (!source.trim()) return [];
  const parse = await loadAcorn();
  if (!parse) return []; // 로드 실패 → 검사 생략(graceful)
  try {
    parse(source, { ecmaVersion: "latest", sourceType: "module", allowReturnOutsideFunction: true });
    return [];
  } catch (e) {
    if (isSyntaxError(e)) {
      const where = e.loc ? `${e.loc.line}번째 줄 ${e.loc.column + 1}열: ` : "";
      // Acorn 메시지는 영어라 한국어 접두 + 원문을 함께 준다(AI 수리에도 유용).
      const msg = e.message.replace(/\s*\(\d+:\d+\)\s*$/, "");
      return [`${where}구문 오류 — ${msg}`];
    }
    return ["구문 오류(원인 미상)"];
  }
}
